/**
 * Asking the analyser for the rungs of a piece.
 *
 * This file used to be a thousand lines that computed them. They live in Go
 * now, in `internal/levels`, and the reason is not tidiness: the rule that
 * numbers a note as a degree of its chord was written twice, once on each side,
 * with a test whose only job was to stop the two from drifting. A third
 * duplication was about to be written for the fingering, where the analyser
 * already runs a solver this side would have answered with a lookup table.
 *
 * What stays on this side is the part Go has no business holding: **the words**.
 * See `words.ts`.
 */

import type { AnalysisHandGrips, AnalysisNote, AnalysisResult } from "@/lib/music/analyzer-types";

/** Where a rung's notes come from. */
export type Realisation = "modele" | "partition";

/**
 * One fact about one chord window, and nothing about how to say it.
 *
 * Rendering into a language belongs here and not to the analyser, for the
 * ordinary reason that it has no opinion about French, and for a sharper one:
 * an observation carrying its own sentence could not be counted, filtered or
 * compared, and the journey does all three.
 */
export type ObservationKind =
  | "inversion"
  | "pulse"
  | "publishedBass"
  | "voices"
  | "texture"
  | "melody"
  | "ornament"
  | "unnamed";

export type FigureDegree = { number: number; alteration?: number };

/**
 * Ce que la main gauche fait de sa basse à l'intérieur d'une fenêtre.
 *
 * Mesuré sur les 3 106 observations de basse que le service publie sur 40
 * morceaux du catalogue : `once` 8,8 %, `repeat` 2,5 %, `octave` 4,5 %,
 * `walks` 61,7 %, `passing` 22,6 %.
 *
 * Sur les **observations** et non sur les fenêtres du niveau, et l'écart n'est
 * pas anodin : une fenêtre sur huit ne produit aucune observation, ce sont
 * celles que le modèle jouait déjà juste, donc les plus simples. Compté sur
 * toutes les fenêtres, `once` monte à 23 % et `walks` tombe à 40 % : ce sont
 * de vrais chiffres, mais ils décrivent une population que personne ne lit.
 *
 * Deux de ces cinq valeurs sont des leçons d'harmonie et trois n'en sont pas.
 * `walks` dit la conduite à l'intérieur de l'accord, `passing` dit qu'elle en
 * sort ; l'octave, elle, est un fait de texture et ne devient pas un événement
 * harmonique parce qu'on l'aurait nommée comme tel.
 *
 * `passing` l'emporte sur `walks` quand les deux s'appliquent, et c'est
 * l'analyseur qui tranche : une note hors de l'accord est le fait le plus fort
 * de la fenêtre, et le seul qu'aucune lecture de la grille ne donne.
 */
export type BassMotion = "once" | "repeat" | "octave" | "walks" | "passing";

export type Observation = {
  kind: ObservationKind;
  chordId: string;
  startTick: number;
  endTick: number;
  measure: number;
  symbol: string;
  /** Notes this window gained. Absent when the rung changed a shape rather
   *  than a content. */
  added?: number;
  /**
   * The pitches this window is about, in the order the rung reads them.
   *
   * On `publishedBass`, `pitches[0]` is the **first struck** note of the
   * window and not its lowest: what the player meets is an order, not a
   * ranking, and the first note is the one under the finger when the window
   * opens. When `motion` is `"walks"`, the rest are the other pitches of the
   * chord the hand reaches, in the order it reaches them.
   */
  pitches?: number[];
  /** The degrees of `pitches` in the chord, aligned index by index. */
  degrees?: FigureDegree[];
  /** How the bass moves inside the window. Only on `publishedBass`. */
  motion?: BassMotion;
  /**
   * Whether the first bass note is one of the chord's own tones. Only on
   * `publishedBass`, and absent on payloads predating the field: an absent
   * value is unknown, never a yes.
   */
  entryChordTone?: boolean;
  /** Ornament types that arrived, by name. */
  figures?: Record<string, number>;
  /** Attack counts, when the observation is about rhythm. */
  before?: number;
  after?: number;
};

export type Level = {
  index: number;
  rungId: string;
  realisation: Realisation;
  /** The right hand is still holding the model because the score gives it
   *  nothing at this rung. */
  scaffolded: boolean;
  notes: AnalysisNote[];
  /**
   * What this rung adds over the one before, chord window by chord window.
   *
   * Empty on the first rung, and **empty on any window a rung leaves
   * untouched** — which is the same fact the journey needs to skip a passage a
   * player has already acquired. Saying what is new and knowing what to skip
   * are one object.
   */
  news: Observation[];
  /**
   * Les prises de **ce niveau**, pas celles du morceau.
   *
   * Le parcours commence chaque main par « où elle se pose » et lit ce champ.
   * Lui donner les prises de la partition sur un niveau qui joue une triade à
   * Do4 montrerait au joueur une position que le niveau ne demande jamais, et
   * c'est justement au niveau fabriqué que l'étape compte le plus, puisque son
   * sujet entier est une main qui ne bouge pas.
   *
   * `null` quand aucune note ne porte de doigt, ce que la charge utile
   * distingue de « les mains ne prennent aucune position ».
   */
  grips: AnalysisHandGrips | null;
};

export type Ladder = {
  parcours: string;
  levels: Level[];
};

/** Which of the two branches to climb. */
export type Branch = "harmonique" | "par-coeur";

/**
 * The ladder's shape, without asking for a note.
 *
 * The stepper needs to know how many rungs there are and what they are called
 * before the player has opened any of them, and that answer does not depend on
 * the piece: a parcours is policy. So it is read off here and the rungs
 * themselves are asked for one at a time.
 *
 * Nine rungs and not ten: `motifs` was measured to move 1.5 % of the windows of
 * the median piece, and 82 % of what it did move `basse` moves again one rung
 * later. A rung that is almost entirely another rung's news is a rung the
 * player climbs twice.
 */
export const LADDER_SHAPE: Record<Branch, readonly string[]> = {
  harmonique: [
    "accords",
    "renversements",
    "pulsation",
    "basse",
    "voicings",
    "texture",
    "melodie",
    "etrangeres",
    "details",
  ],
  "par-coeur": ["morceau"],
};

function payload(analysis: AnalysisResult, branch: Branch) {
  return {
    notes: analysis.notes,
    chords: analysis.chords ?? [],
    figures: analysis.figures ?? [],
    tempo: analysis.tempo,
    melodyTrack: analysis.phrases?.melodyTrack ?? 0,
    handSize: "medium",
    branch,
  };
}

async function ask(body: unknown): Promise<Ladder> {
  const response = await fetch("/api/levels", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const detail = (await response.json().catch(() => null)) as { message?: string } | null;

    throw new Error(detail?.message ?? `l'analyseur a répondu ${response.status}`);
  }

  return (await response.json()) as Ladder;
}

/**
 * Every rung of a piece.
 *
 * The path a caller takes when it means to **store** the ladder: paid once,
 * read for ever. It is not the path for opening one rung, which costs a tenth
 * of it. Measured on the *Comptine*: 3.7 seconds against 0.3 to 0.7.
 *
 * The whole analysis travels in the request because the service is stateless by
 * design: it is handed a reading and gives back the ladder, and whoever asked
 * stores the answer beside the reading it came from.
 */
export async function fetchLadder(analysis: AnalysisResult, branch: Branch = "harmonique"): Promise<Ladder> {
  return ask(payload(analysis, branch));
}

/**
 * One rung, and only the work that rung needs.
 *
 * A rung's notes come from the classification and the cells handed over by
 * then, so nothing about rung eight needs rung three to have been built. The
 * analyser builds the rung before it for the diff and throws it away, which is
 * why this costs one run of the fingering solver rather than ten.
 */
export async function fetchRung(analysis: AnalysisResult, branch: Branch, index: number): Promise<Level> {
  const ladder = await ask({ ...payload(analysis, branch), rung: index });
  const level = ladder.levels[0];

  if (level === undefined) {
    throw new Error(`l'analyseur n'a rendu aucun niveau pour l'indice ${index}`);
  }

  return level;
}

/**
 * One rung, dressed as an analysis the studio can open.
 *
 * The piano roll, the transport, the synth and the MIDI input all consume a
 * `Song`, and a `Song` is built from an `AnalysisResult`. Rather than teach any
 * of them about rungs, a rung is handed back in the shape they already read:
 * everything but the notes is carried over untouched, so a view reading the
 * chord grid keeps reading the real one.
 */
export function asAnalysis(source: AnalysisResult, level: Level): AnalysisResult {
  // Les prises du niveau remplacent celles du morceau, sans quoi l'étape « où
  // la main se pose » montrerait une position que ce niveau ne demande pas.
  return { ...source, notes: level.notes, handGrips: level.grips ?? undefined };
}
